/**
 * Главная страница репо. Жизненный цикл:
 *   1. Параллельно: `/projects` (всегда, чтобы понимать структуру репо)
 *      и `/snapshot-v2` (как только `effectivePath` определён).
 *   2. `effectivePath` =
 *        - `path` из URL, если задан (включая `''` как explicit-корень);
 *        - иначе единственный проект из `/projects`, если он один;
 *        - иначе undefined → ждём, что пользователь выберет в ProjectPicker'е.
 *   3. Picker рендерится только когда projects.length > 1 и в URL нет path.
 *
 * Важно: для single-project (включая `['']`-корень) мы НЕ пишем `?path=` в URL —
 * URL остаётся каноничным. Раньше пытались делать `setPath('')` через useEffect,
 * но `''` falsy и хук удалял параметр → бесконечный лоадер. Теперь auto-resolve
 * живёт в RepoPage без побочного эффекта на URL.
 *
 * Маршрут: /r/:owner/:repo[?ref=&path=]
 */

import { Alert, Box, Center, Loader, Stack, Text } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { loadSnapshot } from '../lib/snapshot-loader';
import { fetchProjects, triggerSync } from '../lib/api-client';
import { CanvasViewPage } from '../components/CanvasViewPage';
import { registerChatContext } from '../stores/chat-store';
import { PrintDialog } from '../components/PrintDialog';
import { ProjectPicker } from '../components/ProjectPicker';
import { StageLoader } from '../components/StageLoader';
import { useRepoUrlState } from '../hooks/use-repo-url-state';
import { useCanvasPageContext } from '../hooks/use-canvas-page-context';
import { useMe } from '../hooks/use-me';

export function RepoPage(): JSX.Element {
    const { owner, repo } = useParams<{ owner: string; repo: string }>();
    const { ref, path, setRef, setPath } = useRepoUrlState();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Auth-gate: анонимных юзеров /api/repo/* отшивает 401 — редиректим на
    // главную с callbackUrl, чтобы после GitHub-OAuth вернуть на эту же
    // страницу (включая ref/path в query).
    const meQ = useMe();
    const isAuthed = !!meQ.data?.user;
    useEffect(() => {
        if (meQ.isLoading) return;
        if (!meQ.data?.user) {
            const callback = location.pathname + location.search;
            navigate(`/?callbackUrl=${encodeURIComponent(callback)}`, { replace: true });
        }
    }, [meQ.isLoading, meQ.data, navigate]);

    const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing'>('idle');
    const [syncError, setSyncError]   = useState<string | null>(null);
    const [printOpen, setPrintOpen]   = useState(false);
    // Запускаем авто-sync один раз после первой загрузки snapshot
    const autoSynced = useRef(false);

    // Ctrl+P / Cmd+P → открыть PrintDialog вместо нативного диалога браузера.
    // На самой PrintPage перехвата нет, чтобы повторная печать работала нативно.
    useEffect(() => {
        function onKey(e: KeyboardEvent) {
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'p') {
                e.preventDefault();
                setPrintOpen(true);
            }
        }
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const projectsQ = useQuery({
        queryKey: ['projects', owner, repo, ref],
        queryFn:  () => fetchProjects({ owner: owner!, repo: repo!, ref }),
        enabled:  !!owner && !!repo && isAuthed,
        retry:    false,
        staleTime: 60_000,
    });

    // effectivePath: URL → projects → undefined.
    // - path !== undefined: пользователь явно указал (включая '' для корня).
    // - иначе: если projects вернул ровно один проект — авто-resolve.
    // - иначе: ждём (loader или picker).
    const projectsList = projectsQ.data?.projects;
    const effectivePath =
        path !== undefined ? path
        : (projectsList && projectsList.length === 1) ? projectsList[0]
        : undefined;

    const snapshotQ = useQuery({
        queryKey: ['snapshot', owner, repo, ref, effectivePath],
        queryFn:  () => loadSnapshot({ owner: owner!, repo: repo!, ref, path: effectivePath }),
        enabled:  !!owner && !!repo && effectivePath !== undefined && isAuthed,
        retry:    false,
    });

    // Контекст для чат-панели: канвас/нарратив из URL-hash, открытая EntityCard.
    // Хук должен вызываться безусловно, поэтому передаём пустой список канвасов,
    // пока snapshot не загружен.
    const chatPageContext = useCanvasPageContext(snapshotQ.data?.canvases);

    // Глобальный чат (App-level) получает контекст страницы через стор —
    // регистрация на каждый рендер, стор сам дедуплицирует.
    useEffect(() => {
        if (owner && repo && effectivePath !== undefined) {
            registerChatContext({ owner, repo, gitRef: ref, path: effectivePath, pageContext: chatPageContext });
        }
    });

    const handleSync = async () => {
        if (!owner || !repo || effectivePath === undefined || syncStatus === 'syncing') return;
        setSyncStatus('syncing');
        setSyncError(null);
        autoSynced.current = true;
        try {
            const result = await triggerSync({ owner, repo, ref, path: effectivePath });
            if (result.changed) {
                queryClient.setQueryData(
                    ['snapshot', owner, repo, ref, effectivePath],
                    result.snapshot,
                );
            }
        } catch (e) {
            setSyncError((e as Error).message);
        } finally {
            setSyncStatus('idle');
        }
    };

    // Авто-sync один раз после первой загрузки — проверяем есть ли новые коммиты
    useEffect(() => {
        if (snapshotQ.data && !autoSynced.current && !!owner && !!repo && effectivePath !== undefined) {
            autoSynced.current = true;
            void handleSync();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snapshotQ.data]);

    // ── состояния отрисовки ──────────────────────────────────────────

    if (!owner || !repo) {
        return <Center mih="60vh"><Text c="dimmed">Нет owner/repo в URL.</Text></Center>;
    }

    // Анонимный заход — пока useMe резолвится или пока эффект редиректит
    // на главную, показываем нейтральный лоадер. Без этой ветки успели бы
    // дёрнуть useQuery'и и моргнуть Alert'ом с ошибкой.
    if (meQ.isLoading || !isAuthed) {
        return <Center mih="60vh"><Loader /></Center>;
    }

    // Если path не явно в URL и projects ещё грузятся → loader.
    if (effectivePath === undefined && projectsQ.isLoading) {
        return <StageLoader owner={owner} repo={repo} gitRef={ref} />;
    }

    if (projectsQ.error) {
        return (
            <Box p="lg">
                <Alert color="red" title="Не удалось получить список проектов">
                    {(projectsQ.error as Error).message}
                </Alert>
            </Box>
        );
    }

    // Projects загрузились, но effectivePath не определился — значит либо
    // 0 проектов, либо >1 (нужен picker).
    if (effectivePath === undefined) {
        const list = projectsList ?? [];
        if (list.length === 0) {
            return (
                <Box p="lg">
                    <Alert color="yellow" title="В репозитории нет pflow-проектов">
                        Не найдено ни одной папки с маркером `.pflow/`.
                        Это либо не pflow-репо, либо проект ещё не инициализирован.
                    </Alert>
                </Box>
            );
        }
        // length > 1 — показываем picker. Клик пишет path в URL.
        return <ProjectPicker projects={list} onPick={setPath} />;
    }

    // effectivePath определён → ждём snapshot.
    if (snapshotQ.isLoading) return <StageLoader owner={owner} repo={repo} gitRef={ref} />;
    if (snapshotQ.error) {
        return (
            <Box p="lg">
                <Alert color="red" title="Ошибка загрузки snapshot">
                    {(snapshotQ.error as Error).message}
                </Alert>
                <Stack mt="md">
                    <Text fz="sm" c="dimmed">
                        Snapshot собирается на pflow-сервере для указанного
                        GitHub-репозитория. Локальные fixture'ы не поддерживаются.
                    </Text>
                </Stack>
            </Box>
        );
    }
    if (!snapshotQ.data) return <Center mih="60vh"><Text c="dimmed">Нет данных.</Text></Center>;

    return (
        <>
            <CanvasViewPage
                snapshot={snapshotQ.data}
                currentRef={ref}
                onRefChange={setRef}
                currentPath={effectivePath}
                onSync={handleSync}
                syncStatus={syncStatus}
                syncError={syncError ?? undefined}
                onPrint={() => setPrintOpen(true)}
            />
            <PrintDialog
                opened={printOpen}
                onClose={() => setPrintOpen(false)}
                snapshot={snapshotQ.data}
                currentRef={ref}
                currentPath={effectivePath}
            />
        </>
    );
}
